import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiVersion, getMeta, ServiceVersionInfo } from '../api/meta';
import '../styles.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DEFAULT_API_VERSIONS: ServiceVersionInfo[] = [
  { id: 'v1', label: 'V1', status: 'current', default: true },
];

export default function PublicDocService() {
  const navigate = useNavigate();
  const { svc = '' } = useParams();
  const [versions, setVersions] = useState<ServiceVersionInfo[]>(DEFAULT_API_VERSIONS);
  const [selectedVersion, setSelectedVersion] = useState<ApiVersion>('v1');
  const title = useMemo(() => labelForService(svc), [svc]);
  const shortDesc = useMemo(() => shortDescriptionForService(svc, selectedVersion), [svc, selectedVersion]);
  const longCopy = useMemo(() => docLongDescriptionForService(svc, selectedVersion), [svc, selectedVersion]);
  const guide = useMemo(() => docGuideForService(svc, selectedVersion), [svc, selectedVersion]);

  useEffect(() => {
    (async () => {
      try {
        const meta = await getMeta();
        const service = (meta.services || []).find((item) => item.id === svc);
        const availableVersions = visibleVersions(
          service?.versions?.length ? service.versions : (meta.apiVersions?.length ? meta.apiVersions : DEFAULT_API_VERSIONS)
        );
        setVersions(availableVersions);
        setSelectedVersion(selectDefaultVersion(availableVersions, meta.defaultApiVersion || 'v1'));
      } catch {
        setVersions(DEFAULT_API_VERSIONS);
      }
    })();
  }, [svc]);

  return (
    <div>
      <Header showPortalLink={false} loginLabel="Developer Portal" />
      <div className="content docs-content" style={{ paddingTop: 24 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button type="button" className="btn secondary" onClick={() => navigate('/docs')}>Back to Services</button>
        </div>

        {shortDesc && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="muted">{shortDesc}</div>
            {versions.length > 1 && (
              <div className="row" style={{ gap: 12, marginTop: 10, alignItems: 'center' }}>
                <label>API Version
                  <select className="txt" value={selectedVersion} onChange={e => setSelectedVersion(e.target.value as ApiVersion)}>
                    {versions.map((version) => (
                      <option key={version.id} value={version.id}>{version.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        {longCopy && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{longCopy}</div>
          </div>
        )}

        {guide && (
          <>
            {guide.audience && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Who this helps</div>
                <div className="muted">{guide.audience}</div>
              </div>
            )}

            {Array.isArray(guide.useCases) && guide.useCases.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Common use cases</div>
                <ul style={{ marginTop: 6, marginBottom: 0 }}>
                  {guide.useCases.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {guide.sampleRequest && (
              <SampleCodeCard title={guide.sampleRequest.title} subtitle={guide.sampleRequest.subtitle} code={applyVersionToCode(guide.sampleRequest.code, selectedVersion)} />
            )}

            {guide.sampleResponse && (
              <SampleCodeCard title={guide.sampleResponse.title} subtitle={guide.sampleResponse.subtitle} code={guide.sampleResponse.code} />
            )}

            {guide.sampleCode && (
              <SampleCodeCard title={guide.sampleCode.title} subtitle={guide.sampleCode.subtitle} code={applyVersionToCode(guide.sampleCode.code, selectedVersion)} />
            )}
          </>
        )}

        {svc === 'forecast-push' && (
          <div>
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Webhook Setup</div>
              <div className="muted">Expose an HTTPS endpoint on your system to receive POSTed JSON from AfricaWeather. Verify the signature before processing.</div>
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600 }}>Headers</div>
                <ul style={{ marginTop: 6, marginBottom: 0 }}>
                  <li><code>Content-Type: application/json</code></li>
                  <li><code>X-AFW-Event: forecast.push</code></li>
                  <li><code>X-AFW-Delivery-Id: dly_...</code></li>
                  <li><code>X-AFW-Timestamp: 1697457322</code> <span className="muted">(UNIX seconds)</span></li>
                  <li><code>X-AFW-Signature: t=1697457322,v1=&lt;hmac_hex_of('&lt;timestamp&gt;.&lt;rawBody&gt;')&gt;</code></li>
                </ul>
              </div>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Sample Push Notification</div>
              <div className="muted" style={{ marginBottom: 8 }}>
                Example JSON body we POST to your webhook. The test endpoint sends event: "forecast.push.test" with a set of rule evaluations.
              </div>
              <pre style={{ background: '#0b1220', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 12, overflow: 'auto' }}>
{`{
  "event": "forecast.push.test",
  "observedAt": "2025-10-16T13:39:21.202Z",
  "data": {
    "tenantId": "270a8899-7321-4270-aa49-5448982082a9",
    "evaluations": [
      {
        "rule": {
          "parameter": "temperature",
          "operator": ">",
          "value": 0,
          "unit": "C",
          "leadHours": 2,
          "locationIndex": 0
        },
        "targetTime": "2025-10-16T15:00:00.000Z",
        "results": [
          { "lat": -26, "lon": 29, "name": "test", "actual": 27.5, "triggered": true }
        ]
      },
      {
        "rule": {
          "parameter": "feelsLike",
          "operator": ">",
          "value": 100,
          "unit": "C",
          "leadHours": 5
        },
        "targetTime": "2025-10-16T18:00:00.000Z",
        "results": [
          { "lat": -26, "lon": 29, "name": "test", "actual": 18.6, "triggered": false },
          { "lat": -24, "lon": 25, "name": "", "actual": 27, "triggered": false }
        ]
      }
    ]
  }
}`}
              </pre>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Sample Webhook Receiver (Node/Express)</div>
              <div className="muted" style={{ marginBottom: 8 }}>
                Verifies the signature and echoes the JSON. Set <code>AFW_WEBHOOK_SECRET</code> to your tenant secret.
              </div>
              <pre style={{ background: '#0b1220', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 12, overflow: 'auto' }}>
{`const express = require('express');
const crypto = require('crypto');
const app = express();

// Capture raw body for signature verification
app.use(express.json({ verify: (req, _res, buf) => { req.rawBody = buf.toString('utf8'); } }));

function isValidSignature(tsHeader, sigHeader, rawBody, secret) {
  const ts = String(tsHeader || '');
  const m = /^t=(\d+),v1=([a-f0-9]{64})$/i.exec(String(sigHeader || ''));
  if (!m) return false;
  const expected = crypto.createHmac('sha256', secret)
    .update(ts + '.' + rawBody, 'utf8')
    .digest('hex');
  try {
    return crypto.timingSafeEqual(Buffer.from(m[2], 'hex'), Buffer.from(expected, 'hex'));
  } catch { return false; }
}

app.post('/receive', (req, res) => {
  const ts = req.get('X-AFW-Timestamp');
  const sig = req.get('X-AFW-Signature');
  const secret = process.env.AFW_WEBHOOK_SECRET || '';
  if (!isValidSignature(ts, sig, req.rawBody || '', secret)) {
    return res.status(400).send('invalid signature');
  }
  console.log('event:', req.get('X-AFW-Event'));
  console.log('deliveryId:', req.get('X-AFW-Delivery-Id'));
  console.log('payload:', req.body);
  res.sendStatus(200);
});

app.listen(3000, () => console.log('listening on http://localhost:3000/receive'));
`}
              </pre>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Sample Webhook Receiver (Python/Flask)</div>
              <div className="muted" style={{ marginBottom: 8 }}>
                Verifies the signature and echoes the JSON. Set <code>AFW_WEBHOOK_SECRET</code> to your tenant secret.
              </div>
              <pre style={{ background: '#0b1220', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 12, overflow: 'auto' }}>
{`from flask import Flask, request
import hmac, hashlib, os, re

app = Flask(__name__)

def is_valid_signature(ts_header, sig_header, raw_body, secret):
    if not ts_header or not sig_header:
        return False
    m = re.match(r'^t=(\d+),v1=([a-f0-9]{64})$', sig_header, re.I)
    if not m:
        return False
    ts = str(ts_header)
    to_sign = f"{ts}.{raw_body.decode('utf-8')}"
    expected = hmac.new(secret.encode('utf-8'), to_sign.encode('utf-8'), hashlib.sha256).hexdigest()
    try:
        return hmac.compare_digest(m.group(2).lower(), expected.lower())
    except Exception:
        return False

@app.route('/receive', methods=['POST'])
def receive():
    raw = request.get_data(cache=False)
    ts = request.headers.get('X-AFW-Timestamp')
    sig = request.headers.get('X-AFW-Signature')
    secret = os.environ.get('AFW_WEBHOOK_SECRET', '')
    if not is_valid_signature(ts, sig, raw, secret):
        return ('invalid signature', 400)
    print('event:', request.headers.get('X-AFW-Event'))
    print('deliveryId:', request.headers.get('X-AFW-Delivery-Id'))
    print('payload:', request.get_json(silent=True))
    return ('', 200)

if __name__ == '__main__':
    app.run(port=3000)
`}
              </pre>
            </div>
          </div>
        )}
      </div>
      <Footer showIndustries={false} />
    </div>
  );
}

type GuideCode = {
  title: string;
  subtitle: string;
  code: string;
};

type ServiceGuide = {
  audience?: string;
  useCases?: string[];
  sampleRequest?: GuideCode;
  sampleResponse?: GuideCode;
  sampleCode?: GuideCode;
};

function SampleCodeCard({ title, subtitle, code }: GuideCode) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div className="muted" style={{ marginBottom: 8 }}>{subtitle}</div>
      <pre style={{ background: '#0b1220', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 12, overflow: 'auto' }}>
{code}
      </pre>
    </div>
  );
}

function applyVersionToCode(code: string, version: ApiVersion) {
  return String(code || '').replace(/\/v1(?=\/|")/g, `/${version}`);
}

function visibleVersions(versions: ServiceVersionInfo[]): ServiceVersionInfo[] {
  const available = (versions || []).filter((version) => version.status !== 'planned');
  return available.length ? available : DEFAULT_API_VERSIONS;
}

function selectDefaultVersion(versions: ServiceVersionInfo[], defaultVersion: ApiVersion): ApiVersion {
  return versions.some((version) => version.id === defaultVersion) ? defaultVersion : (versions[0]?.id || 'v1');
}

function docGuideForService(id: string = '', version: ApiVersion = 'v1'): ServiceGuide | null {
  switch (id) {
    case 'forecast-push':
      return {
        audience: 'Operations teams that want proactive weather alerts delivered to their own systems.',
        useCases: [
          'Alert staff when risky weather is forecast in important locations.',
          'Trigger customer messages for delays, closures, or schedule changes.',
          'Automate workflows in ticketing, dispatch, or incident tools.',
        ],
      };
    case 'current':
      return {
        audience: 'Apps and teams that need a quick view of current weather at one location.',
        useCases: [
          'Show live weather on a dashboard or mobile home screen.',
          'Support field decisions using current wind, humidity, and visibility.',
          'Run simple immediate checks such as rain/no-rain logic.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: 'Current weather for Nairobi.',
          code: `curl -s -G "https://api.<env>.africaweather.com/v1/current" \\
  --data-urlencode "lat=-1.2921" \\
  --data-urlencode "lon=36.8219" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'Trimmed example response.',
          code: `{
  "current": {
    "location": { "lat": -1.2921, "lon": 36.8219, "tz": "Africa/Nairobi" },
    "observedAt": "2026-02-20T10:00:00Z",
    "fields": [
      { "name": "temperature", "value": 23.4 },
      { "name": "relativeHumidity", "value": 66 },
      { "name": "windSpeed", "value": 4.2 },
      { "name": "weatherCode", "value": 3 }
    ]
  }
}`,
        },
        sampleCode: {
          title: 'Sample code (JavaScript)',
          subtitle: 'Convert fields into a simple object.',
          code: `const res = await fetch(
  "https://api.<env>.africaweather.com/v1/current?lat=-1.2921&lon=36.8219",
  { headers: { Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" } }
);
const data = await res.json();
const fields = Object.fromEntries((data.current.fields || []).map((f) => [f.name, f.value]));
console.log(fields.temperature, fields.weatherCode);`,
        },
      };
    case 'forecast-hourly':
      if (version === 'v2') {
        return {
          audience: 'Planning teams that need richer hour-by-hour forecast data over an explicit UTC date-time range.',
          useCases: [
            'Power scheduling and operational tools with a precise hourly date window instead of a rolling hours count.',
            'Feed downstream systems with richer DTN hourly fields such as temperature bounds, humidity, wind, cloud cover, radiation, and precipitation metrics.',
            'Group hourly forecast records by day in apps where date-based navigation is useful.',
          ],
          sampleRequest: {
            title: 'Sample request',
            subtitle: 'Hourly forecast v2 using a UTC date-time range.',
            code: `curl -s -G "https://api.<env>.africaweather.com/v2/forecast/hourly" \\
  --data-urlencode "lat=-26.2041" \\
  --data-urlencode "lon=28.0473" \\
  --data-urlencode "dateFrom=2026-07-09T00:00:00Z" \\
  --data-urlencode "dateTo=2026-07-10T12:00:00Z" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
          },
          sampleResponse: {
            title: 'Sample output',
            subtitle: 'V2 returns forecastHourly.data grouped by day, then by UTC hour.',
            code: `{
  "forecastHourly": {
    "data": {
      "location": { "lat": -26.2041, "lon": 28.0473, "tz": "Africa/Johannesburg" },
      "days": {
        "2026-07-09": {
          "hours": {
            "2026-07-09T13:00:00Z": {
              "airTemp": 20.8,
              "airTempLowerBound": 18.1,
              "airTempNearGround": 31.1,
              "airTempUpperBound": 21.2,
              "totalCloudCover": 64,
              "dewPoint": -4.4,
              "feelsLikeTemp": 20.8,
              "heatIndex": 20.8,
              "mslPressure": 1020.6,
              "precipAmount": 0,
              "precipProb": 0,
              "relativeHumidity": 18,
              "shortWaveRadiation": 638,
              "globalRadiation": 215,
              "sunshineDuration": 39,
              "surfacePressure": 848.9,
              "visibility": 35.29,
              "weatherCode": 4,
              "windDirection": 239,
              "windGust": 9.6,
              "windSpeed": 4.4,
              "uvIndex": 4.8
            }
          }
        }
      }
    }
  }
}`,
          },
          sampleCode: {
            title: 'Sample code (JavaScript)',
            subtitle: 'Read one returned day and inspect a specific hourly timestamp.',
            code: `const res = await fetch(
  "https://api.<env>.africaweather.com/v2/forecast/hourly?lat=-26.2041&lon=28.0473&dateFrom=2026-07-09T00:00:00Z&dateTo=2026-07-10T12:00:00Z",
  { headers: { Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" } }
);
const { forecastHourly } = await res.json();
const hour = forecastHourly.data.days["2026-07-09"]?.hours["2026-07-09T13:00:00Z"];
console.log(hour?.airTemp, hour?.windSpeed);`,
          },
        };
      }
      return {
        audience: 'Planning teams that need hour-by-hour weather guidance up to 240 hours ahead.',
        useCases: [
          'Plan shifts, logistics, and routes over the next few days.',
          'Choose safer windows for outdoor work and events.',
          'Feed near-term forecast signals into alert workflows.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: '72-hour forecast for one point.',
          code: `curl -s -G "https://api.<env>.africaweather.com/v1/forecast/hourly" \\
  --data-urlencode "lat=-26.2041" \\
  --data-urlencode "lon=28.0473" \\
  --data-urlencode "hours=72" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'Each hour has a timestamp and weather fields.',
          code: `{
  "hourly": {
    "data": {
      "location": { "lat": -26.2041, "lon": 28.0473, "tz": "Africa/Johannesburg" },
      "hours": [
        {
          "time": "2026-02-20T12:00:00Z",
          "fields": [
            { "name": "temperature", "value": 27.8 },
            { "name": "precipitationProbability", "value": 10 }
          ]
        }
      ]
    }
  }
}`,
        },
        sampleCode: {
          title: 'Sample code (Python)',
          subtitle: 'Get first forecast hour quickly.',
          code: `import requests

url = "https://api.<env>.africaweather.com/v1/forecast/hourly"
params = {"lat": -26.2041, "lon": 28.0473, "hours": 72}
headers = {"Authorization": "Bearer <access_token>", "X-API-Key": "<your_api_key>"}
data = requests.get(url, params=params, headers=headers, timeout=20).json()
print(data["hourly"]["data"]["hours"][0])`,
        },
      };
    case 'forecast-daily':
      if (version === 'v2') {
        return {
          audience: 'Users who need richer DTN daily forecast data over an explicit date-time window.',
          useCases: [
            'Power planning tools that need full daily weather metrics for a selected date range.',
            'Support analytics, reporting, and operational planning with richer day-level forecast properties.',
            'Expose daily forecast data in apps where a keyed-by-date response is easier to consume than a simple array.',
          ],
          sampleRequest: {
            title: 'Sample request',
            subtitle: 'Daily forecast v2 using a UTC date-time range.',
            code: `curl -s -G "https://api.<env>.africaweather.com/v2/forecast/daily" \\
  --data-urlencode "lat=-33.9249" \\
  --data-urlencode "lon=18.4241" \\
  --data-urlencode "dateFrom=2026-06-25T00:00:00Z" \\
  --data-urlencode "dateTo=2026-06-30T00:00:00Z" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
          },
          sampleResponse: {
            title: 'Sample output',
            subtitle: 'V2 returns forecastDaily.data with days keyed by date.',
            code: `{
  "forecastDaily": {
    "data": {
      "location": { "lat": -33.9249, "lon": 18.4241, "tz": "Africa/Johannesburg" },
      "days": {
        "2026-06-25": {
          "validTime": "2026-06-25T00:00:00Z",
          "airTempMax": 24.8,
          "airTempMin": 14.1,
          "airTempAvg": 19.2,
          "precipProbMax": 20,
          "precipAmountSum": 0.8,
          "windSpeedMax": 7.1,
          "relativeHumidityAvg": 61,
          "weatherCode": 3,
          "sunshineDurationSum": 30120
        }
      }
    }
  }
}`,
          },
          sampleCode: {
            title: 'Sample code (JavaScript)',
            subtitle: 'Read a specific forecast day by date.',
            code: `const res = await fetch(
  "https://api.<env>.africaweather.com/v2/forecast/daily?lat=-33.9249&lon=18.4241&dateFrom=2026-06-25T00:00:00Z&dateTo=2026-06-30T00:00:00Z",
  { headers: { Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" } }
);
const { forecastDaily } = await res.json();
const day = forecastDaily.data.days["2026-06-25"];
console.log(day.airTempMax, day.precipProbMax);`,
          },
        };
      }
      return {
        audience: 'Users who want a simple day-by-day outlook rather than hourly detail.',
        useCases: [
          'Display 7-day and 14-day views in customer products.',
          'Support weekly planning for teams and communities.',
          'Create daily summary reports with fewer data points.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: '7-day daily forecast.',
          code: `curl -s -G "https://api.<env>.africaweather.com/v1/forecast/daily" \\
  --data-urlencode "lat=-33.9249" \\
  --data-urlencode "lon=18.4241" \\
  --data-urlencode "days=7" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'One object per day.',
          code: `{
  "daily": {
    "location": { "lat": -33.9249, "lon": 18.4241, "tz": "Africa/Johannesburg" },
    "days": [
      {
        "date": "2026-02-20",
        "temperatureMin": 18.2,
        "temperatureMax": 28.7,
        "precipitationProbability": 12
      }
    ]
  }
}`,
        },
        sampleCode: {
          title: 'Sample code (JavaScript)',
          subtitle: 'Read first daily item.',
          code: `const res = await fetch(
  "https://api.<env>.africaweather.com/v1/forecast/daily?lat=-33.9249&lon=18.4241&days=7",
  { headers: { Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" } }
);
const { daily } = await res.json();
const first = (daily.days || [])[0];
console.log(first.temperatureMin, first.temperatureMax);`,
        },
      };
    case 'history':
      return {
        audience: 'Analysts and support teams who need weather context from past time periods.',
        useCases: [
          'Review weather during incidents or service disruptions.',
          'Compare past weather against operational outcomes.',
          'Build trend and audit reports using historical snapshots.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: 'Historical period (start/end required, ISO format, maximum 30 days per request).',
          code: `curl -s -G "https://api.<env>.africaweather.com/v1/history" \\
  --data-urlencode "lat=-17.8252" \\
  --data-urlencode "lon=31.0335" \\
  --data-urlencode "start=2026-02-18T00:00:00Z" \\
  --data-urlencode "end=2026-02-19T00:00:00Z" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'History mirrors the forecast-style structure and includes station details.',
          code: `{
  "history": {
    "data": {
      "location": { "lat": -17.8252, "lon": 31.0335, "tz": "Africa/Harare" },
      "station": { "stationCode": "FVHA", "lat": -17.825, "lon": 31.033, "elevation": 1490 },
      "hours": [
        {
          "time": "2026-02-18T00:00:00Z",
          "fields": [
            { "name": "temperature", "value": 20.1 },
            { "name": "relativeHumidity", "value": 64 }
          ]
        }
      ]
    }
  }
}`,
        },
        sampleCode: {
          title: 'Sample code (Python)',
          subtitle: 'Count returned records.',
          code: `import requests

url = "https://api.<env>.africaweather.com/v1/history"
params = {"lat": -17.8252, "lon": 31.0335, "start": "2026-02-18T00:00:00Z", "end": "2026-02-19T00:00:00Z"}
headers = {"Authorization": "Bearer <access_token>", "X-API-Key": "<your_api_key>"}
data = requests.get(url, params=params, headers=headers, timeout=20).json()
print(len(data.get("history", {}).get("data", {}).get("hours", [])))`,
        },
      };
    case 'astronomical':
      return {
        audience: 'Developers and planners who need sun and moon timing for a specific location and short date range.',
        useCases: [
          'Show sunrise, sunset, solar noon, and moon phase information in web or mobile apps.',
          'Support planning for outdoor work, events, travel, photography, and field operations.',
          'Add calendar-aware astronomical context to dashboards and customer-facing tools.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: 'Astronomical data for Johannesburg over a short date range.',
          code: `curl -s -G "https://api.<env>.africaweather.com/v1/astronomical" \\
  --data-urlencode "lat=-26.2041" \\
  --data-urlencode "lon=28.0473" \\
  --data-urlencode "dateFrom=2026-06-12" \\
  --data-urlencode "dateTo=2026-06-14" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'The response is grouped by day and includes sun and moon properties.',
          code: `{
  "astronomical": {
    "data": {
      "location": { "lat": -26.2041, "lon": 28.0473, "tz": "Africa/Johannesburg" },
      "days": {
        "2026-06-12": {
          "sun": {
            "sunrise": "2026-06-12T04:52:08Z",
            "sunset": "2026-06-12T15:24:59Z",
            "solarNoon": "2026-06-12T10:08:33Z"
          },
          "moon": {
            "moonrise": "2026-06-12T03:48:02Z",
            "moonset": "2026-06-12T14:22:10Z",
            "illumination": "29.7%"
          }
        }
      }
    }
  }
}`,
        },
        sampleCode: {
          title: 'Sample code (JavaScript)',
          subtitle: 'Read the first returned day and inspect the sun data.',
          code: `const res = await fetch(
  "https://api.<env>.africaweather.com/v1/astronomical?lat=-26.2041&lon=28.0473&dateFrom=2026-06-12&dateTo=2026-06-14",
  { headers: { Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" } }
);
const data = await res.json();
const days = data.astronomical?.data?.days || {};
const firstDate = Object.keys(days)[0];
console.log(firstDate, days[firstDate]?.sun?.solarNoon);`,
        },
      };
    case 'bulk-current':
      return {
        audience: 'Teams that monitor weather for many sites in one call.',
        useCases: [
          'Update branch, depot, or farm weather in one request.',
          'Power map dashboards with many current conditions at once.',
          'Reduce round trips compared to calling single-location endpoints repeatedly.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: 'Current conditions for multiple locations.',
          code: `curl -s -X POST "https://api.<env>.africaweather.com/v1/bulk/current" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>" \\
  -d '{
    "locations": [
      { "lat": -26.2041, "lon": 28.0473 },
      { "lat": -33.9249, "lon": 18.4241 }
    ]
  }'`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'Each result entry mirrors the current endpoint.',
          code: `{
  "results": [
    { "current": { "location": { "lat": -26.2041, "lon": 28.0473 }, "fields": [{ "name": "temperature", "value": 24.6 }] } },
    { "current": { "location": { "lat": -33.9249, "lon": 18.4241 }, "fields": [{ "name": "temperature", "value": 21.2 }] } }
  ]
}`,
        },
        sampleCode: {
          title: 'Sample code (JavaScript)',
          subtitle: 'POST multiple locations and read result count.',
          code: `const payload = { locations: [{ lat: -26.2041, lon: 28.0473 }, { lat: -33.9249, lon: 18.4241 }] };
const res = await fetch("https://api.<env>.africaweather.com/v1/bulk/current", {
  method: "POST",
  headers: { "Content-Type": "application/json", Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" },
  body: JSON.stringify(payload)
});
const data = await res.json();
console.log((data.results || []).length);`,
        },
      };
    case 'bulk-forecast-hourly':
      return {
        audience: 'Control-room style teams that need forecast coverage for many sites at once.',
        useCases: [
          'Check forecast risk across all monitored locations in one request.',
          'Plan cross-region operations from one consolidated payload.',
          'Feed downstream alerting systems for multiple assets.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: 'Hourly forecast for multiple locations.',
          code: `curl -s -X POST "https://api.<env>.africaweather.com/v1/bulk/forecast/hourly" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>" \\
  -d '{
    "hours": 48,
    "locations": [
      { "lat": -26.2041, "lon": 28.0473 },
      { "lat": -17.8252, "lon": 31.0335 }
    ]
  }'`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'Each result entry mirrors the hourly endpoint.',
          code: `{
  "results": [
    {
      "hourly": {
        "data": {
          "location": { "lat": -26.2041, "lon": 28.0473 },
          "hours": [{ "time": "2026-02-20T12:00:00Z", "fields": [{ "name": "temperature", "value": 27.8 }] }]
        }
      }
    }
  ]
}`,
        },
        sampleCode: {
          title: 'Sample code (Python)',
          subtitle: 'Post a multi-location forecast request.',
          code: `import requests

payload = {"hours": 48, "locations": [{"lat": -26.2041, "lon": 28.0473}, {"lat": -17.8252, "lon": 31.0335}]}
headers = {"Authorization": "Bearer <access_token>", "X-API-Key": "<your_api_key>", "Content-Type": "application/json"}
data = requests.post("https://api.<env>.africaweather.com/v1/bulk/forecast/hourly", json=payload, headers=headers, timeout=20).json()
print(len(data.get("results", [])))`,
        },
      };
    case 'api-credit':
      return {
        audience: 'Admins and planners who need to track limits and usage.',
        useCases: [
          'Show remaining monthly allowance in admin dashboards.',
          'Set alerts when usage approaches limits.',
          'Understand which services consume the most credits.',
        ],
        sampleRequest: {
          title: 'Sample request',
          subtitle: 'Get current credit usage summary.',
          code: `curl -s "https://api.<env>.africaweather.com/v1/credit" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "X-API-Key: <your_api_key>"`,
        },
        sampleResponse: {
          title: 'Sample output',
          subtitle: 'Limits plus usage by service.',
          code: `{
  "tenantId": "tenant-123",
  "month": "February 2026",
  "perMonthMax": 500000,
  "perMinuteDefault": 100,
  "usedMonth": 125430,
  "remainingMonth": 374570,
  "byService": { "current": 40210, "forecast-hourly": 60720 }
}`,
        },
        sampleCode: {
          title: 'Sample code (JavaScript)',
          subtitle: 'Read remaining month credits.',
          code: `const res = await fetch("https://api.<env>.africaweather.com/v1/credit", {
  headers: { Authorization: "Bearer <access_token>", "X-API-Key": "<your_api_key>" }
});
const credit = await res.json();
console.log(credit.remainingMonth);`,
        },
      };
    default:
      return null;
  }
}

function labelForService(id: string = ''): string {
  switch (id) {
    case 'forecast-push': return 'Forecast Push Notifications';
    case 'api-credit': return 'API Credit Service';
    case 'current': return 'Current API';
    case 'forecast-hourly': return 'Forecast Hourly API';
    case 'forecast-daily': return 'Forecast Daily';
    case 'history': return 'History API';
    case 'astronomical': return 'Astronomical API';
    case 'bulk-current': return 'Bulk Current';
    case 'bulk-forecast-hourly': return 'Bulk Forecast Hourly';
    default: return id;
  }
}

function shortDescriptionForService(id: string = '', version: ApiVersion = 'v1'): string {
  switch (id) {
    case 'forecast-push':
      return 'Turn forecast conditions into timely notifications delivered to your webhook.';
    case 'current':
      return 'Real-time conditions at a point.';
    case 'forecast-hourly':
      return version === 'v2'
        ? 'Hourly forecast over a UTC date-time range, with richer DTN hour-level fields.'
        : 'Hourly forecast up to 240 hours.';
    case 'forecast-daily':
      return version === 'v2'
        ? 'Daily forecast over a UTC date-time range, with richer DTN day-level fields.'
        : 'Daily forecast up to 14 days.';
    case 'history':
      return 'Historical conditions over a chosen time range.';
    case 'astronomical':
      return 'Sun and moon information over a short date range.';
    case 'bulk-current':
      return 'Current conditions for multiple locations in one call.';
    case 'bulk-forecast-hourly':
      return 'Hourly forecast for multiple locations in one call.';
    case 'api-credit':
      return 'Retrieve credit usage and configured rate limits.';
    default:
      return '';
  }
}

function docLongDescriptionForService(id: string = '', version: ApiVersion = 'v1'): string {
  switch (id) {
    case 'forecast-push':
      return `
Forecast Push Notifications keep your team in the know - automatically.

Choose the places that matter and set simple weather rules (for example, "temperature above 30C" or "strong winds"). We will check the forecast for you each hour and send a friendly JSON summary to your webhook. You will know when conditions are on track - and just as importantly, when they calm down again - so you can plan with confidence.

What you get:
- Your own webhook gets a signed hourly summary (easy to verify and parse)
- Clear status for each rule and location (met or not met)
- Simple setup: pick locations, add a webhook URL, define rules
- Copy-paste examples to get started fast;
`;
    case 'current':
      return `
Returns real-time weather conditions for a specific latitude/longitude. Typical fields include temperature, humidity, wind, pressure, visibility and a compact weatherCode that describes the sky state (for example: clear, rain, thunder).

Use this when you need "what is it like right now?" for dashboards, mobile apps, on-site displays or trigger logic. Responses are normalized and designed to be cached briefly (around 30-60 seconds) to avoid unnecessary calls in high-traffic UIs.
`;
    case 'forecast-hourly':
      if (version === 'v2') {
        return `
Provides hour-by-hour forecast data using a required UTC date-time range. Version 2 returns a forecastHourly.data payload with location metadata and a days object keyed by date, where each day contains an hours object keyed by UTC timestamp.

The hour objects preserve richer DTN hourly properties such as air temperature, near-ground temperature, confidence bounds, feels-like temperature, heat index, humidity, dew point, cloud cover, pressure, wind, UV index, radiation, precipitation, and weather code fields. Use this version when you need an explicit date window or a fuller hourly dataset than the compact v1 array.
`;
      }
      return `
Provides hour-by-hour forecasts for the next period (commonly 24-240 hours depending on configuration). Each hour includes temperature, precipitation probability or totals, wind and the same weatherCode taxonomy for quick status cues.

Ideal for scheduling, staffing, routing and alerting scenarios where the next few hours matter. Pair with the weather-codes and parameters helper endpoints to dynamically label and format values in your UI.
`;
    case 'forecast-daily':
      if (version === 'v2') {
        return `
Delivers daily forecast data using a required UTC date-time range. Version 2 returns a forecastDaily.data payload with location metadata and a days object keyed by date, making it easier to access daily records directly by date.

The day objects preserve richer DTN daily properties such as air temperature, feels-like temperature, dew point, precipitation, cloud cover, humidity, wind, pressure, visibility, radiation, sunshine duration, and weather code fields. Use this version when you need a fuller daily dataset than the compact v1 summary.
`;
      }
      return `
Delivers daily summaries up to 14 days ahead (configurable). Each day aggregates min/max temperature, precipitation signals and representative conditions. Daily forecasts are compact and easier to display than raw hourly series.

Use daily data for planning pages, reports and notification digests where high-level expectations are sufficient and users do not need hour-resolution detail.
`;
    case 'history':
      return `
Returns historical observations between a start and end time for a given location. The service first resolves the nearest observation station, then returns normalized records for that station.

Each request supports a maximum 30-day range. Returned record spacing depends on available source data and may vary (for example, 1-hour or 3-hour intervals). For longer studies, split your queries into multiple windows and store results in your own system for repeat analysis.
`;
    case 'astronomical':
      return `
Returns location-based astronomical information for each day in the requested date window. The response is normalized into an astronomical.data object with location details and a days map keyed by date.

Typical day entries include sun and moon properties such as sunrise, sunset, solar noon, moonrise, moonset, and illumination. Requests require dateFrom and dateTo in YYYY-MM-DD format, dateFrom must be earlier than dateTo, and each request is limited to a maximum 14-day range.
`;
    case 'bulk-current':
      return `
Fetches current conditions for many locations in a single request. Send an array of {lat, lon} and receive a parallel array of results. This reduces HTTP overhead and improves throughput for fleet and multi-site dashboards.

Keep batches reasonably sized (for example, 50-200 locations per call depending on payload) to balance latency and payload size.
`;
    case 'bulk-forecast-hourly':
      return `
Retrieves hourly forecasts for multiple locations at once. You control the number of hours returned and provide an array of coordinates. This is well-suited to logistics, asset monitoring and operations centers consolidating many sites.

For very large estates, batch your requests and cache stable horizons (for example, the next 6-12 hours) to minimize repeated downloads.
`;
    case 'api-credit':
      return `
Exposes your tenant's credit allocation and rate limits. The response includes overall monthly allowance, default per-minute limits and any service-specific overrides, as well as current usage tallies.

Integrate this endpoint into admin tools to surface remaining credit, drive proactive alerts, or adapt client-side polling intervals when approaching limits.
`;
    default:
      return '';
  }
}
